/**
 * @fileoverview Org portal — instructor detail (Spec 11). Read-only view of one
 * instructor's roster and (per student) their lesson summaries, plus a
 * per-instructor feature-override panel (org default → this-instructor override).
 * Above the tabs, the class meetings they owe notes for and Chase notes, which
 * asks them alone (NotesOwedCard, C4); not drawn when nothing is owed.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { ORG_FEATURES } from "@/lib/orgFeatures";
import { recordedAt, sortChronologically } from "@/lib/lessonHistory";
import { Card, SectionHeader, ListRow, Avatar, Icon, Tag, StatCard, SegmentedControl } from "@/components/studio/primitives";
import NotesOwedCard from "@/components/org/NotesOwedCard";

const PROGRAM_TYPE_LABELS = { private_lessons: "Private lessons", class: "Class", camp: "Camp" };

const filterClass =
  "rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-3 py-2 " +
  "font-sans text-[14px] text-[var(--text-primary)] outline-none focus:border-[var(--accent-yellow)]";

function Toggle({ on, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={on}
      className={`relative shrink-0 rounded-full transition-colors ${on ? "bg-[var(--accent-yellow)]" : "bg-[var(--studio-interactive)]"}`}
      style={{ width: 40, height: 22 }}
    >
      <span
        className="absolute top-0.5 rounded-full bg-[var(--studio-knob)] shadow-sm transition-transform"
        style={{ width: 18, height: 18, left: 2, transform: on ? "translateX(18px)" : "translateX(0)" }}
      />
    </button>
  );
}

export default function OrgInstructorDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [instructor, setInstructor] = useState(null);
  const [features, setFeatures] = useState({});
  const [overrides, setOverrides] = useState({});
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openStudent, setOpenStudent] = useState(null);
  const [lessons, setLessons] = useState({});
  const [teaching, setTeaching] = useState(null);
  const [tab, setTab] = useState("overview");
  const [historyProgram, setHistoryProgram] = useState("all");
  const [historyKind, setHistoryKind] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [detail, roster, taught] = await Promise.all([
        orgApi.get(`/org/instructors/${userId}`),
        orgApi.get(`/org/instructors/${userId}/students`),
        // Scoped to this org — an instructor's private or other-org teaching
        // is not this organization's to show.
        orgApi.get(`/org/instructors/${userId}/teaching`).catch(() => ({ data: null })),
      ]);
      setInstructor(detail.data.instructor);
      setFeatures(detail.data.features || {});
      setOverrides(detail.data.instructor.feature_overrides || {});
      setStudents(roster.data.students || []);
      setTeaching(taught.data);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error("Instructor not found");
        navigate("/org/instructors");
        return;
      }
      toast.error(getOrgErrorMessage(err, "Couldn't load instructor"));
    } finally {
      setLoading(false);
    }
  }, [userId, navigate]);

  useEffect(() => { load(); }, [load]);

  const openLessons = async (studentId) => {
    if (openStudent === studentId) { setOpenStudent(null); return; }
    setOpenStudent(studentId);
    if (!lessons[studentId]) {
      try {
        const res = await orgApi.get(`/org/instructors/${userId}/students/${studentId}/lessons`);
        setLessons((prev) => ({ ...prev, [studentId]: sortChronologically(res.data.lessons, recordedAt) }));
      } catch (err) {
        toast.error(getOrgErrorMessage(err, "Couldn't load lessons"));
      }
    }
  };

  // A per-instructor override cycles: (org default) → force on → force off → reset.
  const cycleOverride = async (key) => {
    const current = key in overrides ? overrides[key] : null; // null = using org default
    const next = current === null ? true : current === true ? false : null;
    const prevOverrides = overrides;
    const optimistic = { ...overrides };
    if (next === null) delete optimistic[key]; else optimistic[key] = next;
    setOverrides(optimistic);
    try {
      const res = await orgApi.put(`/org/instructors/${userId}/features`, { features: { [key]: next } });
      setOverrides(res.data.feature_overrides || {});
      setFeatures(res.data.features || {});
    } catch (err) {
      setOverrides(prevOverrides);
      toast.error(getOrgErrorMessage(err, "Couldn't update"));
    }
  };

  const shownHistory = useMemo(
    () =>
      sortChronologically(
        (teaching?.history || []).filter(
          (h) =>
            (historyProgram === "all" || h.program_id === historyProgram) &&
            (historyKind === "all" || h.kind === historyKind),
        ),
      ),
    [teaching, historyProgram, historyKind],
  );

  if (loading) {
    return <div className="mx-auto max-w-3xl px-4 py-8 md:px-8 font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</div>;
  }
  if (!instructor) return null;

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 md:px-8">
      <button
        type="button"
        onClick={() => navigate("/org/instructors")}
        className="mb-4 inline-flex items-center gap-1 font-sans text-[13px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
      >
        <Icon name="chevronLeft" size={15} /> All instructors
      </button>

      <div className="mb-6 flex items-center gap-3">
        <Avatar name={instructor.name} size={48} />
        <div className="min-w-0">
          <h1 className="truncate font-serif text-2xl font-semibold text-[var(--text-primary)]">{instructor.name || instructor.email}</h1>
          <div className="truncate font-sans text-[14px] text-[var(--text-tertiary)]">{instructor.email}</div>
        </div>
      </div>

      <NotesOwedCard userId={userId} name={instructor.name || instructor.email} />

      <div className="mb-6">
        <SegmentedControl
          testId="instructor-tabs"
          value={tab}
          onChange={setTab}
          options={[
            { id: "overview", label: "Overview" },
            { id: "teaching", label: "Teaching", count: teaching?.totals?.sessions },
          ]}
        />
      </div>

      {tab === "teaching" && (
        <>
          {/* Same counting rule as the program rollup and the weekly emails:
              one session is one taught meeting, whatever the attendance. */}
          <SectionHeader>Taught in this organization</SectionHeader>
          <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatCard value={teaching?.totals?.sessions ?? "—"} label="Sessions" />
            <StatCard value={teaching?.totals?.class_sessions ?? "—"} label="Class sessions" />
            <StatCard value={teaching?.totals?.solo_sessions ?? "—"} label="Solo lessons" />
            <StatCard value={teaching?.totals?.students_reached ?? "—"} label="Students reached" />
          </div>

          <SectionHeader>Programs ({teaching?.programs?.length || 0})</SectionHeader>
          <Card padding={0} className="mb-6">
            {(teaching?.programs || []).length === 0 ? (
              <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">
                Not part of any program in this organization yet.
              </div>
            ) : (
              teaching.programs.map((p, i) => (
                <ListRow
                  key={p.program_id}
                  borderBottom={i < teaching.programs.length - 1}
                  title={p.title}
                  subtitle={PROGRAM_TYPE_LABELS[p.program_type] || p.program_type}
                  meta={p.status === "active" ? "Current" : "Past"}
                  trailing={
                    <span className="font-mono text-[15px] text-[var(--text-primary)]">{p.sessions}</span>
                  }
                  onPress={() => navigate(`/org/programs/${p.program_id}`)}
                />
              ))
            )}
          </Card>

          <SectionHeader>History</SectionHeader>
          <div className="mb-3 flex flex-wrap gap-2">
            <select
              value={historyProgram}
              onChange={(e) => setHistoryProgram(e.target.value)}
              aria-label="Filter history by program"
              data-testid="history-filter-program"
              className={filterClass}
            >
              <option value="all">All programs</option>
              {(teaching?.programs || []).map((p) => (
                <option key={p.program_id} value={p.program_id}>{p.title}</option>
              ))}
            </select>
            <select
              value={historyKind}
              onChange={(e) => setHistoryKind(e.target.value)}
              aria-label="Filter history by kind"
              data-testid="history-filter-kind"
              className={filterClass}
            >
              <option value="all">Classes and lessons</option>
              <option value="class">Classes only</option>
              <option value="solo">Solo lessons only</option>
            </select>
          </div>
          <Card padding={0} className="mb-6">
            {shownHistory.length === 0 ? (
              <div data-testid="history-empty" className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">
                {(teaching?.history || []).length === 0
                  ? "Nothing taught in this organization yet."
                  : "Nothing matches those filters."}
              </div>
            ) : (
              shownHistory.map((h, i) => (
                <ListRow
                  key={h.lesson_id || `${h.date}-${i}`}
                  borderBottom={i < shownHistory.length - 1}
                  title={h.class_name || h.title || (h.kind === "class" ? "Class session" : "Lesson")}
                  subtitle={[h.program_title, h.kind === "class" ? `${h.students} students` : null]
                    .filter(Boolean)
                    .join(" · ")}
                  meta={h.date}
                  trailing={<Tag accent={h.kind === "class"}>{h.kind === "class" ? "Class" : "Solo"}</Tag>}
                />
              ))
            )}
          </Card>
        </>
      )}

      {tab === "overview" && (
        <>
      {/* Per-instructor feature overrides */}
      <Card className="mb-6">
        <SectionHeader>Features for this instructor</SectionHeader>
        <p className="mb-4 font-sans text-[13px] text-[var(--text-tertiary)]">
          Each feature follows the organization default unless you override it here.
        </p>
        <div className="flex flex-col gap-4">
          {ORG_FEATURES.map(({ key, label, desc }) => {
            const overridden = key in overrides;
            const effective = features[key];
            return (
              <div key={key} className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-sans text-[15px] text-[var(--text-primary)]">{label}</span>
                    {overridden && <Tag accent>Override</Tag>}
                  </div>
                  <div className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]">{desc}</div>
                </div>
                <div className="flex items-center gap-3">
                  <Toggle on={!!effective} onClick={() => cycleOverride(key)} testId={`org-override-${key}`} />
                  {overridden && (
                    <button
                      type="button"
                      onClick={() => cycleOverride(key) /* cycles back toward reset */}
                      className="font-sans text-[12px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                    >
                      {overrides[key] ? "on" : "off"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Roster + read-only summaries */}
      <SectionHeader>Students ({students.length})</SectionHeader>
      <Card padding={0}>
        {students.length === 0 ? (
          <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">No students on this roster.</div>
        ) : (
          students.map((s, i) => {
            const name = `${s.first_name} ${s.last_name}`.trim() || "Student";
            const rows = lessons[s.student_id];
            return (
              <div key={s.student_id}>
                <ListRow
                  borderBottom={i < students.length - 1 && openStudent !== s.student_id}
                  leading={<Avatar name={name} size={36} />}
                  title={name}
                  subtitle={s.email || (s.consent_status ? `Consent: ${s.consent_status}` : undefined)}
                  onPress={() => openLessons(s.student_id)}
                  trailing={<Icon name={openStudent === s.student_id ? "chevronUp" : "chevronDown"} size={18} className="text-[var(--text-tertiary)]" />}
                />
                {openStudent === s.student_id && (
                  <div className="border-b border-[var(--studio-divider)] bg-[var(--studio-base)] px-[18px] py-4">
                    {!rows ? (
                      <div className="font-sans text-[13px] text-[var(--text-tertiary)]">Loading lessons…</div>
                    ) : rows.length === 0 ? (
                      <div className="font-sans text-[13px] text-[var(--text-tertiary)]">No lessons yet.</div>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {rows.map((le) => (
                          <div key={le.lesson_id} className="rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-3">
                            <div className="mb-1 font-sans text-[15px] font-medium text-[var(--text-primary)]">{le.title || "Lesson"}</div>
                            {le.summary && <div className="font-sans text-[13px] leading-relaxed text-[var(--text-secondary)]">{le.summary}</div>}
                            {Array.isArray(le.practice_items) && le.practice_items.length > 0 && (
                              <ul className="mt-2 list-disc pl-5 font-sans text-[13px] text-[var(--text-tertiary)]">
                                {le.practice_items.map((p, idx) => <li key={idx}>{p}</li>)}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </Card>
        </>
      )}
    </div>
  );
}
